// ─────────────────────────────────────────────────────────────────────────────
// Endereços e credenciais — um lugar só.
//
// Na versão 1.x isso vivia solto no meio do renderer.js (linhas 390-415). Aqui
// cada valor vem primeiro de uma variável de ambiente (arquivo .env, lido no
// build pelo react-scripts) e, se ela não existir, cai no valor que o Orion já
// usava. Assim nada quebra hoje e dá para trocar por ambiente amanhã.
//
// Regra do react-scripts: só variáveis com o prefixo REACT_APP_ chegam ao app.
// ─────────────────────────────────────────────────────────────────────────────

import { ehWeb } from '../services/plataforma';

/**
 * Valor de configuração, em três camadas — a primeira que responder vence:
 *
 *   1. `window.__ORION_CONFIG__`, escrito pelo container na INICIALIZAÇÃO a
 *      partir das variáveis do painel da hospedagem (ver docker-config.sh);
 *   2. `process.env`, embutido pelo react-scripts na hora do BUILD a partir do
 *      arquivo .env — é o caminho do desktop e do `npm start`;
 *   3. o valor que o Orion já usava, quando existe padrão.
 *
 * A camada 1 existe porque a imagem é construída UMA vez e roda em qualquer
 * ambiente. Sem ela, trocar a chave do Supabase obrigaria a recompilar o bundle
 * inteiro — variáveis REACT_APP_ são embutidas no build, não lidas em execução.
 *
 * Quando a camada 1 não existe (desktop, `npm start`, testes), `__ORION_CONFIG__`
 * é undefined e tudo se comporta exatamente como antes.
 */
function env(nome, padrao) {
  const doRuntime = (typeof window !== 'undefined' && window.__ORION_CONFIG__)
    ? window.__ORION_CONFIG__[nome]
    : undefined;
  if (doRuntime !== undefined && doRuntime !== '') return doRuntime;

  const doBuild = process.env[nome];
  if (doBuild !== undefined && doBuild !== '') return doBuild;

  return padrao;
}

// ── Webhooks do n8n ──────────────────────────────────────────────────────────
export const WEBHOOKS = {
  rag:                 env('REACT_APP_WEBHOOK_RAG',                 'https://n8n.space-cloud.tech/webhook/dev-entrada-dados'),
  helpdesk:            env('REACT_APP_WEBHOOK_HELPDESK',            'https://n8n.space-cloud.tech/webhook/helpdesk-v2'),
  // formalização do "criar novo ticket"
  helpdeskV3:          env('REACT_APP_WEBHOOK_HELPDESK_V3',         'https://n8n.space-cloud.tech/webhook/helpdesk-v3'),
  // Formalização rápida: só o texto, sem ticket e sem classificação. É a
  // alternativa enxuta ao fluxo de três fases — ver services/formalizacaoRapida.js.
  formalizadorRapido:  env('REACT_APP_WEBHOOK_FORMALIZADOR_RAPIDO',  'https://n8n.space-cloud.tech/webhook/formalizador-nexus'),
  classificacao:       env('REACT_APP_WEBHOOK_CLASSIFICACAO',       'https://n8n.space-cloud.tech/webhook/cat'),
  // Matriz GUT: fluxo próprio, que responde TEXTO (não JSON) — ver MatrizGut.
  gut:                 env('REACT_APP_WEBHOOK_GUT',                 'https://n8n.space-cloud.tech/webhook/GUT'),
  auth:                env('REACT_APP_WEBHOOK_AUTH',                'https://n8n.space-cloud.tech/webhook/login'),
  erros:               env('REACT_APP_WEBHOOK_ERROS',               'https://n8n.space-cloud.tech/webhook/erros'),
  errosSalvar:         env('REACT_APP_WEBHOOK_ERROS_SALVAR',        'https://n8n.space-cloud.tech/webhook/salvar-erro'),
  errosBuscar:         env('REACT_APP_WEBHOOK_ERROS_BUSCAR',        'https://n8n.space-cloud.tech/webhook/busca-erro'),
  atualizarTicket:     env('REACT_APP_WEBHOOK_ATUALIZA_TICKET',     'https://n8n.space-cloud.tech/webhook/OrionAtualiza'),
  contatoSemSucesso:   env('REACT_APP_WEBHOOK_CONTATO_SEM_SUCESSO', 'https://n8n.space-cloud.tech/webhook/OrionContatoSemSucesso'),
  inserirTicket:       env('REACT_APP_WEBHOOK_INSERE_TICKET',       'https://n8n.space-cloud.tech/webhook/OrionCria'),
  emAtendimento:       env('REACT_APP_WEBHOOK_EM_ATENDIMENTO',      'https://n8n.space-cloud.tech/webhook/OrionAtualizaEmAtendimento'),
  gerarDocumento:      env('REACT_APP_WEBHOOK_GERAR_DOCUMENTO',     'https://n8n.space-cloud.tech/webhook/gerar-documento'),
  // Passa o texto do atendimento por um agente antes de virar documento: ele
  // reescreve em tom didático (o PDF vai para o CLIENTE) e limpa as tags que
  // sobram do editor. Ver services/conclusaoDocumento.js.
  conclusaoForm:       env('REACT_APP_WEBHOOK_CONCLUSAO_FORM',      'https://n8n.space-cloud.tech/webhook/conclusao-form'),
};

// Canal do Teams (via Power Automate) avisado quando o atendimento vai para o
// Comercial. Disparado direto pelo Orion, sem passar pelo n8n.
//
// SEM VALOR PADRÃO, de propósito. Esta URL carrega uma assinatura (`sig=`) que
// é, na prática, a credencial do fluxo: quem a tiver posta mensagens naquele
// canal. No 1.x ela estava escrita no renderer.js, ou seja, versionada no Git.
// Sem a variável de ambiente, o aviso ao Comercial simplesmente não é enviado
// (ver helpdeskApi.avisarComercialNoTeams) — o atendimento segue normalmente.
export const TEAMS_COMERCIAL_WEBHOOK = env('REACT_APP_WEBHOOK_TEAMS_COMERCIAL', '');

// ── API do MySuite ───────────────────────────────────────────────────────────
// Aponta direto para HTTPS de propósito: em http o servidor responde redirect
// (301 no GET, 308 no POST). O fetch seguiria, mas cada chamada pagaria um salto
// extra e o primeiro envio sairia sem TLS.
export const MYSUITE_API_BASE = env(
  'REACT_APP_MYSUITE_API_URL',
  'https://apimysuite.space-cloud.tech/apimysuite'
);

// ── Supabase ─────────────────────────────────────────────────────────────────
//
// SEM VALOR PADRÃO. No 1.x a chave estava escrita no renderer.js e versionada
// no Git — e não era uma chave anon: era uma **service_role**, que ignora todo
// o RLS. Quem tivesse o arquivo lia e escrevia qualquer tabela do projeto.
//
// Agora ela vem do .env (que não é versionado). Isso NÃO a esconde do bundle
// final — variáveis REACT_APP_ são embutidas no build e ficam visíveis no app.
// O que resolve de verdade é a chave ter privilégio mínimo: uma anon, com RLS
// ligado nas tabelas. Ver verificarChaveSupabase(), no fim deste arquivo.
export const SUPABASE_URL = env('REACT_APP_SUPABASE_URL', '');
export const SUPABASE_KEY = env('REACT_APP_SUPABASE_ANON_KEY', '');

// A tabela de erros vive no schema "orion" (não em "public") no Supabase configurado
export const ORION_SCHEMA = 'orion';

// ── Gemini (embedding da busca de incidentes) ────────────────────────────────
//
// SEM VALOR PADRÃO, e de preferência VAZIA.
//
// Uma chave de API do Google embutida no app é cota de graça para quem a
// extrair. O caminho certo é o embedding ser gerado no n8n, que já tem o
// webhook de busca de incidentes — o app manda o texto, o servidor guarda a
// chave. É o que services/incidentesApi.js faz quando
// REACT_APP_WEBHOOK_ERROS_BUSCAR está configurado.
//
// Esta variável existe só como saída de emergência, para quando o webhook não
// estiver disponível. Preenchê-la volta a expor a chave no bundle.
export const GEMINI_API_KEY = env('REACT_APP_GEMINI_API_KEY', '');
export const GEMINI_MODELO_PADRAO = env('REACT_APP_GEMINI_MODELO', 'gemini-2.5-flash-lite');

// ─────────────────────────────────────────────────────────────────────────────
// AVISO DE SEGURANÇA — leia antes de publicar o Orion na web.
//
// A chave do Supabase herdada da versão 1.x é uma **service_role**, não uma
// **anon**. Uma service_role ignora TODAS as regras de RLS: quem a tiver lê e
// escreve qualquer tabela do projeto.
//
// No desktop empacotado ela já era extraível (basta abrir o asar), mas num
// bundle web ela fica a um Ctrl+U de distância de qualquer visitante.
//
// O que precisa ser feito antes de o modo web ir para produção:
//   1. Gerar uma chave **anon** no painel do Supabase;
//   2. Ligar RLS nas tabelas do schema `orion` com as policies de leitura/escrita;
//   3. Trocar REACT_APP_SUPABASE_ANON_KEY pela anon;
//   4. Revogar a service_role atual (ela já circulou em config.json e no bundle).
//
// A checagem abaixo não conserta nada — só grita no console para o problema não
// passar despercebido.
// ─────────────────────────────────────────────────────────────────────────────
export function verificarChaveSupabase() {
  if (!SUPABASE_URL || !SUPABASE_KEY) {
    console.error(
      '[Orion] Supabase não configurado. Preencha REACT_APP_SUPABASE_URL e '
      + 'REACT_APP_SUPABASE_ANON_KEY no arquivo .env (ver .env.example). '
      + 'Sem isso, incidentes, listas e chamados de T.I. não carregam.'
    );
    return false;
  }

  try {
    const corpo = JSON.parse(atob(SUPABASE_KEY.split('.')[1]));
    if (corpo.role === 'service_role') {
      const aviso = '[Orion] A chave do Supabase em uso é service_role — ela ignora RLS. '
        + 'Gere uma chave anon, ligue RLS nas tabelas do schema orion e revogue esta.';
      if (ehWeb) console.error(aviso);
      else console.warn(aviso);
      return false;
    }
  } catch (e) { /* chave em formato inesperado — segue */ }
  return true;
}

// ── Categorias (produtos) ────────────────────────────────────────────────────
// O mapa mora em config/categorias.js — sem dependências, para as regras puras
// poderem usá-lo fora do app. Aqui só é reexportado, para nada quebrar.
export { MAPA_CATEGORIAS } from './categorias';

// Mensagem padrão de conclusão do atendimento (o operador pode trocar nas configurações)
export const MENSAGEM_CONCLUSAO_PADRAO = 'Atendimento concluído. Validado com o solicitante.';
